"use client";

import { useState, useEffect } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { RotateCcw, Volume2, Paintbrush, ArrowRight, Mic } from "lucide-react";
import { useProfileContext } from "../../../context/ProfileContext";
import TaskProgress, { type TaskStatus } from "./TaskProgress";
import {
  getToyShelfProgress,
  TOY_SLOT_EMOJIS,
  TOY_SLOT_NAMES,
  TOY_TARGET_PER_SLOT,
  type ToyShelfProgress,
} from "../../../services/toyCollection";

type RewardStage =
  | "tasks"
  | "merge"
  | "factory"
  | "gift"
  | "rocket"
  | "shelf"
  | "done";

const EMPTY_TOY_PROGRESS: ToyShelfProgress = {
  slots: Array.from({ length: TOY_SLOT_EMOJIS.length }, () => 0),
  lastCollectedSlot: null,
};

const scaleMs = (ms: number, reducedMotion: boolean): number => {
  if (reducedMotion) {
    return Math.max(180, Math.round(ms * 0.65));
  }
  return Math.round(ms * 1.3);
};

type RewardFlowAnimationProps = {
  reducedMotion: boolean;
  toyProgress: ToyShelfProgress;
  onComplete: () => void;
  onSkip: () => void;
  taskStatus: { english: boolean; telugu: boolean; drawing: boolean };
  showToyCollection: boolean;
};

const RewardFlowAnimation = ({
  reducedMotion,
  toyProgress,
  onComplete,
  onSkip,
  taskStatus,
  showToyCollection,
}: RewardFlowAnimationProps) => {
  const [stage, setStage] = useState<RewardStage>("tasks");
  const [completedTasks, setCompletedTasks] = useState([false, false, false]);
  
  const deliveredToyEmoji =
    toyProgress.lastCollectedSlot !== null
      ? TOY_SLOT_EMOJIS[toyProgress.lastCollectedSlot]
      : "🧸";

  const targetCompletedCount = [taskStatus.english, taskStatus.telugu, taskStatus.drawing].filter(Boolean).length;
  const totalRequiredTasks = 3; 
  const isEligibleForReward = targetCompletedCount === totalRequiredTasks;

  useEffect(() => {
    if (stage !== "tasks") return;

    const t1 = window.setTimeout(
      () => setCompletedTasks([taskStatus.english, false, false]),
      scaleMs(700, reducedMotion)
    );
    const t2 = window.setTimeout(
      () => setCompletedTasks([taskStatus.english, taskStatus.telugu, false]),
      scaleMs(1400, reducedMotion)
    );
    const t3 = window.setTimeout(
      () => setCompletedTasks([taskStatus.english, taskStatus.telugu, taskStatus.drawing]),
      scaleMs(2100, reducedMotion)
    );

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [stage, reducedMotion, taskStatus]);

  useEffect(() => {
    if (stage !== "tasks" || completedTasks.filter(Boolean).length !== targetCompletedCount) return;

    const timer = window.setTimeout(() => {
      if (isEligibleForReward) {
        setStage("merge");
      } else {
        onComplete();
      }
    }, scaleMs(500, reducedMotion));

    return () => window.clearTimeout(timer);
  }, [completedTasks, stage, reducedMotion, isEligibleForReward, targetCompletedCount, onComplete]);

  useEffect(() => {
    if (stage !== "merge") return;
    const timer = window.setTimeout(() => setStage("factory"), scaleMs(650, reducedMotion));
    return () => window.clearTimeout(timer);
  }, [stage, reducedMotion]);

  useEffect(() => {
    if (stage !== "factory") return;
    const timer = window.setTimeout(() => setStage("gift"), scaleMs(1800, reducedMotion));
    return () => window.clearTimeout(timer);
  }, [stage, reducedMotion]);

  useEffect(() => {
    if (stage !== "gift") return;
    const timer = window.setTimeout(() => setStage("rocket"), scaleMs(1800, reducedMotion));
    return () => window.clearTimeout(timer);
  }, [stage, reducedMotion]);

  useEffect(() => {
    if (stage !== "rocket") return;
    const timer = window.setTimeout(() => setStage("shelf"), scaleMs(2400, reducedMotion));
    return () => window.clearTimeout(timer);
  }, [stage, reducedMotion]);

  useEffect(() => {
    if (stage !== "shelf") return;
    const timer = window.setTimeout(() => setStage("done"), scaleMs(900, reducedMotion));
    return () => window.clearTimeout(timer);
  }, [stage, reducedMotion]);

  useEffect(() => {
    if (stage !== "done") return;
    onComplete();
  }, [stage, onComplete]);

  return (
    <div className="relative mx-auto w-full max-w-[560px] overflow-hidden rounded-3xl border border-white/70 bg-white/70 p-4 shadow-xl backdrop-blur-sm sm:p-6">
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-4">
        <AnimatePresence mode="wait">
          {(stage === "tasks" || stage === "merge") && (
            <motion.div
              key="tasks"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92 }}
              className="flex flex-col items-center gap-4"
            >
              {stage === "tasks" && (
                <div className="flex items-center gap-5">
                  {[
                    { icon: "🎤", label: "English", done: taskStatus.english },
                    { icon: "🎤", label: "Telugu", done: taskStatus.telugu },
                    { icon: "✏️", label: "Drawing", done: taskStatus.drawing },
                  ].map((task, i) => (
                    <motion.div
                      key={task.label}
                      className="flex flex-col items-center gap-2"
                      animate={
                        completedTasks.every(Boolean) && isEligibleForReward
                          ? { x: i === 0 ? 56 : i === 2 ? -56 : 0, scale: 0.86 }
                          : {}
                      }
                      transition={{ duration: reducedMotion ? 0.25 : 0.6, ease: "easeInOut" }}
                    >
                      <motion.div
                        className={`relative flex h-16 w-16 items-center justify-center rounded-full text-2xl shadow-md ${
                          completedTasks[i] && task.done ? "bg-emerald-500" : "bg-slate-200"
                        }`}
                        animate={completedTasks[i] && task.done ? { scale: [1, 1.2, 1] } : {}}
                        transition={{ duration: reducedMotion ? 0.2 : 0.4 }}
                      >
                        {task.icon}
                        {completedTasks[i] && task.done && (
                          <span className="absolute -right-1 -top-1 text-lg text-amber-500">⭐</span>
                        )}
                      </motion.div>
                      <span className="text-xs font-bold text-slate-600">{task.label}</span>
                    </motion.div>
                  ))}
                </div>
              )}

              {stage === "merge" && (
                <motion.div
                  className="h-20 w-20 rounded-full bg-gradient-to-br from-amber-400 to-blue-500 shadow-2xl"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: [0, 1.28, 1], opacity: 1 }}
                  transition={{ duration: reducedMotion ? 0.3 : 0.6 }}
                />
              )}
            </motion.div>
          )}

          {stage === "factory" && (
            <motion.div
              key="factory"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="relative flex flex-col items-center"
            >
              <div className="relative h-36 w-60 rounded-2xl border-2 border-slate-300 bg-gradient-to-b from-slate-100 to-slate-200">
                <motion.div
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 text-4xl"
                  animate={{ rotate: 360 }}
                  transition={{ duration: reducedMotion ? 0.9 : 2, repeat: Infinity, ease: "linear" }}
                >
                  ⚙️
                </motion.div>
              </div>
              <motion.div
                className="mt-3 text-5xl"
                initial={{ opacity: 0, y: -20, scale: 0 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: reducedMotion ? 0.2 : 0.8, duration: reducedMotion ? 0.25 : 0.45 }}
              >
                🎁
              </motion.div>
            </motion.div>
          )}

          {stage === "gift" && (
            <motion.div
              key="gift"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex flex-col items-center gap-3"
            >
              <motion.div
                className="text-7xl"
                animate={{ rotate: [0, -8, 8, -8, 8, 0] }}
                transition={{ duration: reducedMotion ? 0.6 : 1, repeat: Infinity }}
              >
                🎁
              </motion.div>
              <motion.div
                className="text-5xl"
                initial={{ y: 14, scale: 0, opacity: 0 }}
                animate={{ y: 0, scale: [0, 1.25, 1], opacity: 1 }}
                transition={{ delay: reducedMotion ? 0.25 : 0.55, duration: reducedMotion ? 0.3 : 0.6 }}
              >
                {deliveredToyEmoji}
              </motion.div>
            </motion.div>
          )}

          {stage === "rocket" && (
            <motion.div
              key="rocket"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative h-44 w-full max-w-[420px] overflow-hidden rounded-2xl bg-gradient-to-b from-sky-100 to-sky-200"
            >
              <motion.div
                className="absolute left-0 top-0"
                initial={{ x: -120, y: 128, rotate: -18, opacity: 0 }}
                animate={{ x: 250, y: 34, rotate: 4, opacity: 1 }}
                transition={{ duration: reducedMotion ? 1.4 : 2.8, ease: "easeInOut" }}
              >
                <div className="relative text-6xl">
                  <span aria-hidden>🚀</span>
                  <motion.span
                    className="absolute -top-5 left-1/2 -translate-x-1/2 text-3xl"
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: reducedMotion ? 0.8 : 1.1, repeat: Infinity }}
                  >
                    {deliveredToyEmoji}
                  </motion.span>
                </div>
              </motion.div>
              <motion.span
                className="absolute left-0 top-0 text-3xl"
                initial={{ x: -140, y: 140, opacity: 0 }}
                animate={{ x: 210, y: 72, opacity: [0, 0.9, 0] }}
                transition={{ duration: reducedMotion ? 1.2 : 2.4, ease: "easeInOut" }}
              >
                💨
              </motion.span>
              <p className="absolute bottom-3 left-0 right-0 text-center text-sm font-bold text-slate-600">
                {showToyCollection ? "Flying to your toy shelf..." : "Flying in with your toy..."}
              </p>
            </motion.div>
          )}

          {stage === "shelf" && (
            <motion.div
              key="shelf"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="w-full rounded-2xl border border-slate-200 bg-white p-4 shadow"
            >
              {showToyCollection ? (
                <>
                  <p className="mb-3 text-center text-sm font-bold text-slate-600">🏆 My Toy Collection</p>
                  <div className="grid grid-cols-5 gap-2">
                    {TOY_SLOT_EMOJIS.map((emoji, slotIndex) => {
                      const count = toyProgress.slots[slotIndex] ?? 0;
                      const isLatest = toyProgress.lastCollectedSlot === slotIndex;
                      return (
                        <motion.div
                          key={`${emoji}-${slotIndex}`}
                          className={`flex h-12 items-center justify-center rounded-lg border text-xl ${
                            count > 0 ? "border-sky-200 bg-sky-50" : "border-slate-200 bg-slate-100"
                          } ${isLatest ? "border-emerald-300 bg-emerald-50" : ""}`}
                          animate={isLatest ? { scale: [1, 1.12, 1] } : {}}
                          transition={{ duration: reducedMotion ? 0.2 : 0.45 }}
                        >
                          {count > 0 ? emoji : <span className="font-extrabold text-slate-500">?</span>}
                        </motion.div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center gap-3 py-3">
                  <motion.div
                    className="flex h-28 w-28 items-center justify-center rounded-full bg-amber-100 text-7xl shadow-inner"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: reducedMotion ? 0.35 : 0.7 }}
                  >
                    {deliveredToyEmoji}
                  </motion.div>
                  <p className="text-center text-sm font-bold text-slate-600">Here is your toy!</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <p className="mt-3 text-center text-sm font-bold text-slate-600">
        {stage === "tasks" && "Evaluating tasks..."}
        {stage === "merge" && "Combining your achievements!"}
        {stage === "factory" && "Making your toy..."}
        {stage === "gift" && "Revealing your reward..."}
        {stage === "rocket" && "Delivering your toy..."}
        {stage === "shelf" && (showToyCollection ? "Adding to your collection..." : "Showing your reward...")}
      </p>

      <button
        type="button"
        onClick={onSkip}
        className="absolute inset-0 z-20 cursor-pointer"
        aria-label="Skip reward animation"
      >
        <span className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/35 px-3 py-1 text-xs font-semibold text-white/90">
          Tap to skip
        </span>
      </button>
    </div>
  );
};

// Drawing Success Feedback Screen
const DrawingSuccessFeedback = ({
  onDrawAgain,
  onNextWord,
  onPracticeAgain,
  onPracticeEnglish,
  onPracticeTelugu,
  taskStatus,
  progressStatus,
  startAtResult = false,
  showToyCollection = false,
}: any) => {
  const prefersReducedMotion = useReducedMotion();
  const reducedMotion = Boolean(prefersReducedMotion);
  const [isAnimationComplete, setIsAnimationComplete] = useState(startAtResult);
  const [showContent, setShowContent] = useState(false);
  const [showSentence, setShowSentence] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const { currentWord, selectedProfile } = useProfileContext();
  const [toyProgress, setToyProgress] = useState<ToyShelfProgress>(EMPTY_TOY_PROGRESS);
  const wonToySlot = toyProgress.lastCollectedSlot ?? TOY_SLOT_EMOJIS.length - 1;
  
  const wonToyName =
    wonToySlot !== null
      ? TOY_SLOT_NAMES[wonToySlot] ?? "Toy"
      : "Toy";
  const wonToyEmoji = TOY_SLOT_EMOJIS[wonToySlot] ?? "🧸";

  useEffect(() => {
    let isMounted = true;
    void (async () => {
      const progress = await getToyShelfProgress(selectedProfile?.studentId);
      if (isMounted) {
        setToyProgress(progress);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [selectedProfile?.studentId]);

  useEffect(() => {
    if (!startAtResult) return;
    setIsAnimationComplete(true);
  }, [startAtResult]);

  const completeAnimation = () => {
    setIsAnimationComplete((prev) => (prev ? prev : true));
  };

  useEffect(() => {
    if (!isAnimationComplete) return;
    const timer1 = setTimeout(() => setShowContent(true), 200);
    const timer2 = setTimeout(() => setShowSentence(true), 450);
    const timer3 = setTimeout(() => setShowButtons(true), 700);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [isAnimationComplete]);

  const isEverythingComplete = taskStatus.english && taskStatus.telugu && taskStatus.drawing;
  const displayingToyShelf = showToyCollection && isEverythingComplete;
  const displayingReward = isEverythingComplete;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 via-blue-50 to-indigo-100 p-4">
      <div className="relative mx-auto w-[92vw] max-w-[560px] overflow-hidden">
        {!isAnimationComplete && (
          <RewardFlowAnimation
            reducedMotion={reducedMotion}
            toyProgress={toyProgress}
            onComplete={completeAnimation}
            onSkip={completeAnimation}
            taskStatus={taskStatus}
            showToyCollection={showToyCollection}
          />
        )}

        {isAnimationComplete && (
          <div className="z-10">
            <div className="mx-auto w-full max-w-[640px] rounded-3xl bg-white/80 p-6 text-center shadow-2xl backdrop-blur-sm sm:p-8">
              {progressStatus && <TaskProgress taskStatus={progressStatus} />}

              <div className="relative mb-6">
                <div
                  className={`transform transition-all duration-1000 ${
                    showContent ? "scale-100 rotate-0" : "scale-0 rotate-180"
                  }`}
                >
                  <div className="mb-4 flex flex-col items-center justify-center gap-4">
                    <h1 className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent text-5xl font-bold sm:text-6xl">
                      {displayingReward ? "Woohoo!" : "Great Job"}
                    </h1>
                    <img
                      src="/success.gif"
                      alt="Happy Owl"
                      className="h-32 w-32 rounded-full shadow-lg"
                    />
                  </div>
                </div>
              </div>

              <div
                className={`transform transition-all duration-700 delay-300 ${
                  showContent ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                }`}
              >
                <p className="mb-6 text-2xl font-medium text-green-600 sm:text-3xl">
                  {displayingReward ? "You got it all right!" : `Good try! That looks like a ${currentWord?.english ?? "word"}`}
                </p>
                {displayingReward && (
                  <p className="mb-4 text-lg font-semibold text-purple-700 sm:text-2xl">
                    You won a {wonToyName}!
                  </p>
                )}
              </div>

              <div
                className={`transform transition-all duration-700 delay-700 ${
                  showSentence ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                }`}
              >
                {displayingReward ? (
                  <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
                    {displayingToyShelf ? (
                      <div className="mx-auto max-w-[420px] rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                        <p className="mb-3 text-sm font-bold text-slate-600">🏆 My Toy Collection</p>
                        <div className="grid grid-cols-5 gap-2">
                          {TOY_SLOT_EMOJIS.map((emoji, slotIndex) => {
                            const count = toyProgress.slots[slotIndex] ?? 0;
                            const isLatest = toyProgress.lastCollectedSlot === slotIndex;

                            return (
                              <div key={`${emoji}-${slotIndex}`} className="text-center">
                                <div
                                  className={`mb-1 flex h-14 w-full items-center justify-center rounded-xl border text-2xl transition-all duration-300 ${
                                    count > 0 ? "border-sky-200 bg-sky-50" : "border-slate-200 bg-slate-100"
                                  } ${isLatest ? "scale-105 border-emerald-300 bg-emerald-50 shadow" : ""}`}
                                >
                                  {count > 0 ? emoji : <span className="text-slate-500 font-extrabold">?</span>}
                                </div>
                                <p className="text-xs font-semibold text-slate-600">
                                  {count}/{TOY_TARGET_PER_SLOT}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="mx-auto flex max-w-[320px] flex-col items-center rounded-2xl border border-amber-200 bg-white p-5 shadow-sm">
                        <div className="mb-3 flex h-28 w-28 items-center justify-center rounded-full bg-amber-100 text-7xl shadow-inner">
                          {wonToyEmoji}
                        </div>
                        <p className="text-sm font-bold text-slate-600">Your reward</p>
                        <p className="text-xl font-extrabold text-purple-700">{wonToyName}</p>
                      </div>
                    )}

                    <div className="mt-4 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-100 to-blue-200 p-3 sm:p-4">
                      <p className="text-left text-base text-gray-700 sm:text-lg sm:leading-8">
                        {currentWord?.sentence_en ?? "I use this word in a sentence."}
                        <br />
                        {currentWord?.sentence_te ?? ""}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mb-6 rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-100 to-blue-200 p-4 sm:p-5">
                    <p className="text-left text-lg text-gray-700 sm:text-2xl sm:leading-10">
                      {currentWord?.sentence_en ?? "I use this word in a sentence."}
                      <br />
                      {currentWord?.sentence_te ?? ""}
                    </p>
                  </div>
                )}
              </div>

              <div
                className={`transform transition-all duration-700 delay-1000 ${
                  showButtons ? "translate-y-0 scale-100 opacity-100" : "translate-y-4 scale-95 opacity-0"
                }`}
              >
                <div className="flex flex-wrap justify-center gap-3">
                  {!taskStatus.english && (
                    <button
                      onClick={onPracticeEnglish}
                      className="group flex items-center gap-2 rounded-2xl bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                    >
                      <Mic size={18} className="transition-transform duration-300 group-hover:scale-110" />
                      Practice English
                    </button>
                  )}

                  {!taskStatus.telugu && (
                    <button
                      onClick={onPracticeTelugu}
                      className="group flex items-center gap-2 rounded-2xl bg-gradient-to-r from-pink-500 to-pink-600 px-4 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                    >
                      <Mic size={18} className="transition-transform duration-300 group-hover:scale-110" />
                      Practice Telugu
                    </button>
                  )}

                  {!isEverythingComplete && (
                    <button
                      onClick={onDrawAgain}
                      className="group flex items-center gap-2 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                    >
                      <Paintbrush size={18} className="transition-transform duration-300 group-hover:rotate-12" />
                      Redraw Canvas
                    </button>
                  )}

                  {(isEverythingComplete || !taskStatus.english || !taskStatus.telugu) && (
                    <>
                    {isEverythingComplete && (
                      <button
                        onClick={onPracticeAgain}
                        className="group flex items-center gap-2 rounded-2xl bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                      >
                        <RotateCcw size={18} className="transition-transform duration-500 group-hover:rotate-180" />
                        Practice Again
                      </button>
                    )}

                    <button
                      onClick={onNextWord}
                      className="group flex items-center gap-2 rounded-2xl bg-gradient-to-r from-green-500 to-green-600 px-4 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                    >
                      <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                      Next Word
                    </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Drawing Error Feedback Screen
const DrawingErrorFeedback = ({ onTryAgain, onSkip, progressStatus }: any) => {
  const [showContent, setShowContent] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const { currentWord } = useProfileContext();

  useEffect(() => {
    const timer1 = setTimeout(() => setShowContent(true), 300);
    const timer2 = setTimeout(() => setShowButtons(true), 1000);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 p-4">
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 max-w-lg w-full text-center border border-white/20">
        {progressStatus && <TaskProgress taskStatus={progressStatus} />}

        <div className="relative mb-6">
          <div className={`transform transition-all duration-1000 ${showContent ? "scale-100 rotate-0" : "scale-0 -rotate-180"}`}>
            <div className="flex flex-col items-center justify-center gap-4 mb-4">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-red-500 to-pink-500 bg-clip-text text-transparent">
                Try Again
              </h1>
              <img
                src="/try-again.gif"
                alt="Sad Owl"
                className="w-32 h-32 rounded-full shadow-lg animate-bounce"
              />
            </div>
          </div>
        </div>

        <div className={`transform transition-all duration-700 delay-300 ${showContent ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <p className="text-lg text-orange-600 font-medium mb-6">
            Try again. Remember what a {currentWord!.english} looks like.
          </p>
        </div>

        <div className={`transform transition-all duration-700 delay-700 ${showButtons ? "translate-y-0 opacity-100 scale-100" : "translate-y-4 opacity-0 scale-95"}`}>
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={onTryAgain}
              className="group bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <RotateCcw size={18} className="group-hover:rotate-180 transition-transform duration-500" />
              Try Again
            </button>

            <button
              onClick={onSkip}
              className="group bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <Volume2 size={18} className="group-hover:scale-110 transition-transform duration-300" />
              Skip Word
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

type DrawingFeedbackProps = {
  isSuccess: boolean;
  taskStatus: { english: boolean; telugu: boolean; drawing: boolean };
  progressStatus?: TaskStatus;
  showRewardAnimation?: boolean;
  showToyCollection?: boolean;
  onDrawAgain?: () => void;
  onNextWord?: () => void;
  onPracticeAgain?: () => void;
  onPracticeEnglish?: () => void;
  onPracticeTelugu?: () => void;
  onSkip?: () => void;
};

const DrawingFeedback = (props: DrawingFeedbackProps) => {
  const [currentScreen, setCurrentScreen] = useState("success");

  const {
    isSuccess,
    taskStatus = { english: false, telugu: false, drawing: isSuccess },
    progressStatus,
    showRewardAnimation = false,
    showToyCollection = false,
    onDrawAgain,
    onNextWord,
    onPracticeAgain,
    onPracticeEnglish,
    onPracticeTelugu,
    onSkip,
  } = props;

  useEffect(() => {
    setCurrentScreen(isSuccess ? "success" : "error");
  }, [isSuccess]);

  return (
    <div>
      {currentScreen === "success" && (
        <DrawingSuccessFeedback
          onDrawAgain={onDrawAgain}
          onNextWord={onNextWord}
          onPracticeAgain={onPracticeAgain}
          onPracticeEnglish={onPracticeEnglish}
          onPracticeTelugu={onPracticeTelugu}
          taskStatus={taskStatus}
          progressStatus={progressStatus}
          startAtResult={!showRewardAnimation}
          showToyCollection={showToyCollection}
        />
      )}

      {currentScreen === "error" && (
        <DrawingErrorFeedback onTryAgain={onDrawAgain} onSkip={onSkip} progressStatus={progressStatus} />
      )}
    </div>
  );
};

export default DrawingFeedback;
